import React, { useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as servicesApi from '../services/servicesApi';
import useModalBackButton from '../hooks/useModalBackButton';
import ModalShell from './ModalShell';
import { ModalListSkeleton } from './Skeleton';
import { haptic } from '../utils/vibration';
import { getServiceIconComponent } from '../utils/serviceIcons';

/**
 * Модалка выбора доп. услуг заказа (extra_order)
 * - Группы в виде аккордеона
 * - Услуги без группы — списком ниже с заголовком
 * - Переключатель сразу добавляет/удаляет услугу
 */
const OrderServiceModal = ({ open, onClose, onAdd, onRemove, clientType = 'b2c', existingItems = [], priceAt = null }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [openGroups, setOpenGroups] = useState({});
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open) return;
    const existing = new Set();
    existingItems.forEach(item => {
      if (item.isAdditional && item.additionalType === 'order' && item.service_id) {
        existing.add(item.service_id);
      }
    });
    setSelectedIds(existing);
  }, [open, existingItems]);

  useEffect(() => {
    if (open) {
      loadData();
      setOpenGroups({});
    }
  }, [open, clientType, priceAt]);

  const loadData = async () => {
    try {
      setLoading(true);
      const servicesData = await servicesApi.getServices({
        active_only: true,
        client_type: clientType,
        service_type: 'extra_order',
        price_at: priceAt
      });
      setServices(servicesData);
    } catch (err) {
      console.error('Ошибка загрузки доп. услуг:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (service) => {
    const wasSelected = selectedIds.has(service.id);
    haptic(wasSelected ? 'tap' : 'success');

    // Вычисляем что нужно удалить ДО setState
    let toRemoveIds = [];
    if (!wasSelected && service.allow_multiple === false && service.group_id) {
      services.forEach(s => {
        if (s.group_id === service.group_id && s.id !== service.id && selectedIds.has(s.id)) {
          toRemoveIds.push(s.id);
        }
      });
    }

    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(service.id)) {
        next.delete(service.id);
      } else {
        toRemoveIds.forEach(id => next.delete(id));
        next.add(service.id);
      }
      return next;
    });

    // Сразу добавляем/удаляем
    if (!wasSelected) {
      onAdd({
        service_id: service.id,
        service_system_code: service.system_code || null,
        name: service.name,
        price: service.current_price || service.base_price,
        unit: service.unit,
        icon: service.icon || null,
        quantity: 1,
        isAdditional: true,
        additionalType: 'order',
        unitType: service.current_unit_type || 'rub'
      });
      toRemoveIds.forEach(id => onRemove(id));
    } else {
      onRemove(service.id);
    }
  };

  const toggleGroup = (groupId) => {
    haptic(openGroups[groupId] ? 'close' : 'open');
    setOpenGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Группируем
  const groupsMap = {};
  const ungrouped = [];
  services.forEach(s => {
    if (s.group_id && s.group_name) {
      if (!groupsMap[s.group_id]) {
        groupsMap[s.group_id] = { id: s.group_id, name: s.group_name, allow_multiple: s.allow_multiple !== false, services: [] };
      }
      groupsMap[s.group_id].services.push(s);
    } else {
      ungrouped.push(s);
    }
  });
  const groups = Object.values(groupsMap);

  const renderService = (service) => {
    const isSelected = selectedIds.has(service.id);
    const price = service.current_price || service.base_price;
    const priceLabel = service.current_unit_type === 'percent'
      ? `${price}% от суммы`
      : `${price} ₽`;

    return (
      <div
        key={service.id}
        className={`flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors ${
          isSelected ? 'bg-green-50 dark:bg-green-900/20' : ''
        }`}
      >
        <div className="mr-3 flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-300">
            {(() => {
              const ServiceIcon = getServiceIconComponent(service.icon);
              return <ServiceIcon className="h-4.5 w-4.5" />;
            })()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-gray-900 dark:text-gray-100">{service.name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{priceLabel}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => toggleService(service)}
          className="toggle-switch shrink-0"
          style={{
            '--toggle-bg': isSelected ? 'rgb(168 85 247)' : 'rgb(209 213 219)',
            '--toggle-height': '22px',
            '--toggle-width': '42px',
            '--knob-x': isSelected ? '20px' : '2px'
          }}
        >
          <span className="toggle-switch-knob" />
        </button>
      </div>
    );
  };

  return (
    <ModalShell
      open={open}
      onClose={() => {
        haptic('close');
        onClose();
      }}
      variant="sheet"
      panelClassName="modal-bottom-sheet flex min-h-0 max-h-[75vh] flex-col overflow-hidden sm:max-w-lg"
      panelProps={{ style: { maxHeight: '75dvh', overflowY: 'hidden' } }}
    >
        {/* Заголовок */}
        <div className="sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Доп. услуги заказа</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {clientType === 'b2b' ? 'Юр. лица' : 'Физ. лица'}
            </p>
          </div>
          <button onClick={() => { haptic('close'); onClose(); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading ? (
            <ModalListSkeleton count={4} />
          ) : services.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">Нет доступных услуг</p>
          ) : (
            <div className="space-y-3">
              {/* Группы — аккордеон */}
              {groups.map(group => (
                <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleGroup(group.id); }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {(() => {
                        const previewService = group.services.find((service) => service.icon);
                        const GroupIcon = getServiceIconComponent(previewService?.icon);
                        return <GroupIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />;
                      })()}
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{group.name}</span>
                      {!group.allow_multiple && (
                        <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full">Только одна</span>
                      )}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${openGroups[group.id] ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {openGroups[group.id] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="bg-white dark:bg-gray-900">
                          {group.services.map(renderService)}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              {/* Услуги без группы — с заголовком */}
              {ungrouped.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Без группы</span>
                  </div>
                  {ungrouped.map(renderService)}
                </div>
              )}
            </div>
          )}
        </div>
    </ModalShell>
  );
};

export default OrderServiceModal;
